<script setup lang="ts">
import { Components } from '@/api/generated/api';
import CheckMarkIcon from '@/assets/icons/CheckMarkIcon.vue';
import CustomAccordion from '@/core/elements/CustomAccordion.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import { IUserWithPermissions } from '@/interfaces';
import { computed, toRefs } from 'vue';
import UserCard from '../UserCard.vue';

const props = withDefaults(
  defineProps<{
    user: IUserWithPermissions;
    mode?: 'edit' | 'archive';
  }>(),
  {
    mode: 'edit',
  },
);
const { user } = toRefs(props);

const hasNoPermissions = computed<boolean>(() => {
  return (
    !user.value.permissions.canManageStudents &&
    !user.value.permissions.canManageCompetences &&
    !user.value.permissions.canManagePerformanceAssessments &&
    !user.value.permissions.canManageAccessRights
  );
});

const schoolSubjects = computed<Components.Schemas.BaseSchoolSubjectDto[]>(() => {
  return user.value.schoolSubjects;
});
const isInvitation = computed(() => user.value.isInvitation === true);
</script>

<template>
  <CustomAccordion
    ref="accordionRef"
    :can-clear="true"
    :identifier="`user-access-right-display-list-item-${user.user.id}`"
    class="mb-sm"
  >
    <template #header>
      <div class="flex w-full min-w-0 justify-between gap-sm overflow-hidden pr-md">
        <UserCard :user="user.user" title-class="min-w-0 max-w-full truncate" class="min-w-0 flex-1 overflow-hidden">
          <span v-if="!isInvitation" :title="user.user.email ?? ''" class="block min-w-0 max-w-full truncate">
            {{ user?.user.email }}
          </span>
          <div v-else class="text-muted">{{ $t('user.table.invitationPending') }}</div>
        </UserCard>

        <div class="flex shrink-0 items-center gap-xs">
          <SchoolSubjectBadge v-if="schoolSubjects.length" :school-subject="schoolSubjects[0]" />
          <div v-if="schoolSubjects.length > 1" class="text-body font-normal">+ {{ schoolSubjects.length - 1 }}</div>
          <div v-if="!schoolSubjects.length" class="text-body font-normal">
            {{ $t('accessRights.allSubjects') }}
          </div>
        </div>
      </div>
    </template>
    <template #items>
      <div v-if="user" class="flex flex-col gap-sm pr-md pb-md pl-md md:pl-[84px]">
        <template v-if="mode === 'edit'">
          <div v-if="user.permissions.canManageStudents" class="flex gap-sm">
            <CheckMarkIcon class="size-cell-icon shrink-0" />
            {{ $t('accessRights.canManageStudents') }}
          </div>
          <div v-if="user.permissions.canManageCompetences" class="flex gap-sm">
            <CheckMarkIcon class="size-cell-icon shrink-0" />
            {{ $t('accessRights.canManageCompetences') }}
          </div>
          <div v-if="user.permissions.canManagePerformanceAssessments" class="flex gap-sm">
            <CheckMarkIcon class="size-cell-icon shrink-0" />
            {{ $t('accessRights.canManagePerformanceAssessments') }}
          </div>
          <div v-if="user.permissions.canManageAccessRights" class="flex gap-sm">
            <CheckMarkIcon class="size-cell-icon shrink-0" />
            {{ $t('accessRights.canManageAccessRights') }}
          </div>
        </template>
        <template v-else>
          <div v-if="user.permissions.canManageAccessRights" class="flex gap-sm">
            <CheckMarkIcon class="size-cell-icon shrink-0" />
            {{ $t('accessRights.canManageArchiveRights') }}
          </div>
        </template>
        <div v-if="hasNoPermissions" class="text-muted pl-xl">
          {{ $t('accessRights.hasNoPermissions') }}
        </div>
      </div>
    </template>
  </CustomAccordion>
</template>
